import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { SiteHeader } from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Brain, Pencil, Flask, ClockCounterClockwise, Plus, Trash, FloppyDisk,
  CheckCircle, Warning, MagnifyingGlass, X, ArrowsClockwise,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* =========================================================================
 *  Centralized Hazard Intelligence — admin/assessor workspace.
 *
 *  Four tabs:
 *   • Klimisch        — reliability scoring (1-4) per ingredient
 *   • Endpoints       — hazard endpoint toggles (sensitisation, genotox, etc.)
 *   • Contaminants    — CRUD over the Risk Memo pathway regexes
 *   • Audit           — cross-ingredient activity feed
 *
 *  All endpoints live under /api/admin/hazard/*.
 * =======================================================================*/

const KLIMISCH_OPTIONS = [
  { score: 1, label: 'Reliable without restriction', tone: 'bg-[#DEF7EC] text-[#03543F] border-[#31C48D]' },
  { score: 2, label: 'Reliable with restriction',    tone: 'bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]' },
  { score: 3, label: 'Not reliable',                  tone: 'bg-[#FFE5D9] text-[#9A3412] border-[#FDBA8C]' },
  { score: 4, label: 'Not assignable',                tone: 'bg-[#F3F4F6] text-[#4B5563] border-[#D1D5DB]' },
];
const CONFIDENCE_OPTIONS = ['Reliable', 'Reliable-with-restriction', 'Not-reliable', 'Not-assignable', 'Uncertain'];

function KlimischChip({ score }) {
  const meta = KLIMISCH_OPTIONS.find(o => o.score === score) || KLIMISCH_OPTIONS[3];
  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded font-black text-[11px] tabular-nums border ${meta.tone}`}>
      {score || 4}
    </span>
  );
}

/* ---------------- KLIMISCH TAB ---------------- */

function KlimischTab() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState('');
  const [onlyUnassigned, setOnlyUnassigned] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ score: 1, confidence: 'Reliable', rationale: '', study: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (onlyUnassigned) params.set('only_unassigned', 'true');
      params.set('skip', skip); params.set('limit', limit);
      const r = await axios.get(`${API_URL}/api/admin/hazard/klimisch/queue?${params}`, { withCredentials: true });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setCounts(r.data.counts || {});
    } catch (e) { /* keep prev */ }
    finally { setLoading(false); }
  }, [q, onlyUnassigned, skip]);

  useEffect(() => {
    const h = setTimeout(load, 300);
    return () => clearTimeout(h);
  }, [load]);
  useEffect(() => { setSkip(0); }, [q, onlyUnassigned]);

  const startEdit = (item) => {
    setSelected(item);
    setForm({
      score: item.klimisch_score || 1,
      confidence: item.evidence_confidence || 'Reliable',
      rationale: item.klimisch_rationale || '',
      study: item.klimisch_study_citation || '',
    });
    setError('');
  };

  const save = async () => {
    if (!selected) return;
    if (!form.rationale || form.rationale.trim().length < 4) { setError('Rationale is required (min 4 chars).'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/hazard/klimisch/${encodeURIComponent(selected.inci_name)}/set`, {
        klimisch_score: form.score,
        evidence_confidence: form.confidence,
        rationale: form.rationale.trim(),
        study_citation: form.study?.trim() || null,
      }, { withCredentials: true });
      setSelected(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 card-flat p-4 bg-white">
        {/* Coverage banner */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          {[
            { label: 'Score 1 (reliable)', value: counts.score_1 || 0, tone: 'text-[#0E9F6E]' },
            { label: 'Score 2', value: counts.score_2 || 0, tone: 'text-[#D97706]' },
            { label: 'Score 3', value: counts.score_3 || 0, tone: 'text-[#9A3412]' },
            { label: 'Pending (4 / unset)', value: counts.score_4_or_unset || 0, tone: 'text-[#4B5563]' },
          ].map(c => (
            <div key={c.label} className="bg-[#F9FAFB] p-2 rounded">
              <p className="text-[9px] font-bold uppercase tracking-wider text-[#4B5563]">{c.label}</p>
              <p className={`text-lg font-black tabular-nums ${c.tone}`}>{c.value.toLocaleString()}</p>
            </div>
          ))}
        </div>
        {/* Search */}
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search INCI / CAS…" className="h-8 text-sm pl-8" data-testid="klimisch-search" />
          </div>
          <label className="flex items-center gap-1.5 text-[11px] text-[#4B5563]">
            <input type="checkbox" checked={onlyUnassigned} onChange={(e) => setOnlyUnassigned(e.target.checked)} data-testid="klimisch-only-unassigned" />
            Only unassigned
          </label>
        </div>
        {/* Table */}
        <div className="border border-gray-200 rounded overflow-hidden max-h-[480px] overflow-y-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">
                <th className="text-center py-2 px-2 w-10">K</th>
                <th className="text-left py-2 px-3">INCI</th>
                <th className="text-left py-2 px-3">CAS</th>
                <th className="text-left py-2 px-3">Function</th>
                <th className="text-left py-2 px-3">Confidence</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr><td colSpan={5} className="py-6 text-center text-[#4B5563]">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} className="py-6 text-center text-[#4B5563]">No matches</td></tr>
              ) : items.map(it => (
                <tr key={it.inci_name} onClick={() => startEdit(it)} className={`border-t border-gray-100 cursor-pointer ${selected?.inci_name === it.inci_name ? 'bg-[#FFE9EE]' : 'hover:bg-gray-50'}`} data-testid={`klimisch-row-${it.inci_name}`}>
                  <td className="py-1.5 px-2 text-center"><KlimischChip score={it.klimisch_score} /></td>
                  <td className="py-1.5 px-3 font-semibold text-[#111827]">{it.inci_name}</td>
                  <td className="py-1.5 px-3 font-mono text-[10px] text-[#4B5563]">{it.cas_number || '—'}</td>
                  <td className="py-1.5 px-3 text-[#4B5563]">{it.function_class || '—'}</td>
                  <td className="py-1.5 px-3 text-[#4B5563] text-[10px]">{it.evidence_confidence || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between text-[11px] text-[#4B5563] mt-2">
          <span className="tabular-nums">{total === 0 ? '0' : `${skip + 1}–${Math.min(skip + limit, total)} of ${total.toLocaleString()}`}</span>
          <div className="flex gap-1">
            <Button variant="outline" disabled={skip === 0} onClick={() => setSkip(s => Math.max(0, s - limit))} className="h-7 text-xs rounded" data-testid="klimisch-prev">← Prev</Button>
            <Button variant="outline" disabled={skip + limit >= total} onClick={() => setSkip(s => s + limit)} className="h-7 text-xs rounded" data-testid="klimisch-next">Next →</Button>
          </div>
        </div>
      </div>

      {/* Editor */}
      <div className="card-flat p-4 bg-white" data-testid="klimisch-editor">
        {!selected ? (
          <div className="text-center py-10 text-[#4B5563] text-sm">Pick an ingredient to assign a Klimisch score.</div>
        ) : (
          <>
            <h3 className="text-sm font-bold text-[#111827] mb-0.5">{selected.inci_name}</h3>
            <p className="text-[11px] text-[#4B5563] font-mono mb-3">CAS {selected.cas_number || '—'}</p>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Score</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1 mb-3">
              {KLIMISCH_OPTIONS.map(o => (
                <button key={o.score} type="button" onClick={() => setForm(f => ({ ...f, score: o.score }))} className={`text-left p-2 rounded border text-[11px] ${form.score === o.score ? o.tone : 'border-gray-200 bg-white'}`} data-testid={`klimisch-score-${o.score}`}>
                  <p className="font-bold">{o.score} · {o.label}</p>
                </button>
              ))}
            </div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Evidence confidence</Label>
            <select value={form.confidence} onChange={(e) => setForm(f => ({ ...f, confidence: e.target.value }))} className="mt-1 mb-3 w-full h-8 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="klimisch-confidence">
              {CONFIDENCE_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Rationale</Label>
            <Textarea value={form.rationale} onChange={(e) => setForm(f => ({ ...f, rationale: e.target.value }))} rows={4} className="text-[12px] mt-1 mb-3" placeholder="e.g. OECD 408 90-day rat oral · GLP · NOAEL 100 mg/kg/d · SCCS Opinion 2019" data-testid="klimisch-rationale" />
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Study citation (optional)</Label>
            <Input value={form.study} onChange={(e) => setForm(f => ({ ...f, study: e.target.value }))} className="text-[12px] mt-1 mb-3" placeholder="DOI / SCCS Opinion ref" data-testid="klimisch-study" />
            {error && <p className="text-[11px] text-[#9B1C1C] mb-2" data-testid="klimisch-error">{error}</p>}
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving} className="flex-1 h-9 rounded bg-[#FF4E6A] hover:bg-[#E63E5A] text-white text-sm font-semibold" data-testid="klimisch-save">
                <FloppyDisk size={14} className="mr-1.5" weight="bold" /> {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button onClick={() => setSelected(null)} variant="outline" className="h-9 text-sm rounded">Cancel</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- HAZARD ENDPOINTS TAB ---------------- */

const BOOL_FIELDS = [
  { key: 'genotoxicity', label: 'Genotoxicity', danger: true },
  { key: 'mutagenicity', label: 'Mutagenicity', danger: true },
  { key: 'carcinogenic', label: 'Carcinogenicity', danger: true },
  { key: 'reproductive_toxic', label: 'Reproductive toxicity', danger: true },
  { key: 'phototoxic', label: 'Phototoxicity', danger: true },
  { key: 'eu_annex_ii', label: 'EU Annex II (banned)', danger: true },
  { key: 'eu_annex_iii', label: 'EU Annex III (restricted)', danger: false },
  { key: 'ifra_restricted', label: 'IFRA restricted', danger: false },
  { key: 'prop_65', label: 'California Prop 65', danger: true },
];

function EndpointsTab() {
  const [inciInput, setInciInput] = useState('Phenoxyethanol');
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [rationale, setRationale] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const fetchIng = async (inci) => {
    if (!inci?.trim()) return;
    setLoading(true); setError(''); setMsg(''); setData(null);
    try {
      const r = await axios.get(`${API_URL}/api/admin/hazard/endpoints/${encodeURIComponent(inci.trim())}`, { withCredentials: true });
      setData(r.data);
      setDraft(r.data.fields || {});
    } catch (e) {
      setError(e?.response?.data?.detail || 'Lookup failed');
    } finally { setLoading(false); }
  };

  const dirty = useMemo(() => {
    if (!data) return false;
    for (const k of Object.keys(draft)) if (draft[k] !== data.fields[k]) return true;
    return false;
  }, [data, draft]);

  const save = async () => {
    if (!data) return;
    if (!rationale || rationale.trim().length < 4) { setError('Rationale required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { rationale: rationale.trim() };
      for (const k of Object.keys(draft)) {
        if (draft[k] !== data.fields[k]) payload[k] = draft[k];
      }
      const r = await axios.put(`${API_URL}/api/admin/hazard/endpoints/${encodeURIComponent(data.inci_name)}`, payload, { withCredentials: true });
      if (r.data.saved) {
        setMsg(`Saved · ${Object.keys(r.data.diff || {}).length} field(s) changed`);
        setRationale('');
        await fetchIng(data.inci_name);
      } else {
        setMsg('No changes detected.');
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="card-flat p-5 bg-white">
      <div className="flex gap-2 mb-4">
        <Input value={inciInput} onChange={(e) => setInciInput(e.target.value)} placeholder="Enter exact INCI name…" className="h-9 text-sm" data-testid="endpoints-inci-input" onKeyDown={(e) => e.key === 'Enter' && fetchIng(inciInput)} />
        <Button onClick={() => fetchIng(inciInput)} disabled={loading} className="h-9 rounded bg-[#0B1426] hover:bg-[#1B2538] text-white text-sm" data-testid="endpoints-lookup">
          <MagnifyingGlass size={14} className="mr-1.5" weight="bold" /> {loading ? 'Loading…' : 'Lookup'}
        </Button>
      </div>
      {error && <div className="text-[11px] text-[#9B1C1C] bg-[#FDE8E8] border border-[#F8B4B4] rounded p-2 mb-3" data-testid="endpoints-error">{error}</div>}
      {data && (
        <>
          <div className="mb-3 flex items-center gap-3">
            <h3 className="text-base font-bold text-[#111827]">{data.inci_name}</h3>
            <span className="text-[11px] text-[#4B5563] font-mono">CAS {data.cas_number || '—'}</span>
            <span className="ml-auto"><KlimischChip score={data.klimisch_score} /></span>
          </div>
          {/* Categorical fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Skin sensitisation</Label>
              <select value={draft.skin_sensitization || 'None'} onChange={(e) => setDraft(d => ({ ...d, skin_sensitization: e.target.value }))} className="mt-1 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="endpoints-sensitisation">
                {['None', 'Mild', 'Weak', 'Moderate', 'Strong', 'Extreme'].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Function class</Label>
              <Input value={draft.function_class || ''} onChange={(e) => setDraft(d => ({ ...d, function_class: e.target.value }))} className="mt-1 h-9 text-sm" data-testid="endpoints-function" />
            </div>
          </div>
          {/* Boolean toggles */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
            {BOOL_FIELDS.map(f => {
              const val = !!draft[f.key];
              return (
                <button key={f.key} type="button" onClick={() => setDraft(d => ({ ...d, [f.key]: !val }))} className={`text-left px-3 py-2 rounded border text-[12px] transition-colors ${val ? (f.danger ? 'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]' : 'bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]') : 'bg-white border-gray-200 text-[#4B5563]'}`} data-testid={`endpoints-toggle-${f.key}`}>
                  <p className="font-semibold">{f.label}</p>
                  <p className="text-[10px] mt-0.5 opacity-75">{val ? 'YES' : 'NO'} · click to toggle</p>
                </button>
              );
            })}
          </div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Rationale for changes (required)</Label>
          <Textarea value={rationale} onChange={(e) => setRationale(e.target.value)} rows={3} className="text-[12px] mt-1 mb-3" placeholder="e.g. SCCS Opinion 2024 updates skin sensitisation category from Weak to Moderate based on local lymph node assay (LLNA EC3 ≤ 10%)" data-testid="endpoints-rationale" />
          {msg && <p className="text-[11px] text-[#03543F] bg-[#DEF7EC] border border-[#31C48D] rounded p-2 mb-2" data-testid="endpoints-msg">{msg}</p>}
          <Button onClick={save} disabled={saving || !dirty} className="h-9 rounded bg-[#FF4E6A] hover:bg-[#E63E5A] text-white text-sm font-semibold" data-testid="endpoints-save">
            <FloppyDisk size={14} className="mr-1.5" weight="bold" /> {saving ? 'Saving…' : `Save changes (${Object.keys(draft).filter(k => draft[k] !== data.fields[k]).length})`}
          </Button>

          {/* Recent edits */}
          {data.hazard_audit?.length > 0 && (
            <div className="mt-5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-1.5">Recent edits for this ingredient</p>
              <div className="space-y-1.5">
                {data.hazard_audit.slice().reverse().map((a, i) => (
                  <div key={i} className="bg-[#F9FAFB] rounded p-2 text-[11px] text-[#4B5563]">
                    <p><strong>{a.by}</strong> · {new Date(a.at).toLocaleString()}</p>
                    <p className="text-[10px] opacity-80">{Object.keys(a.changes || {}).join(', ')}</p>
                    {a.rationale && <p className="italic mt-0.5">"{a.rationale}"</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ---------------- CONTAMINANTS TAB ---------------- */

const SEVERITY_OPTIONS = ['high', 'medium', 'low'];
const COLOR_OPTIONS = ['red', 'amber', 'grey'];

function ContaminantsTab() {
  const [pathways, setPathways] = useState([]);
  const [editing, setEditing] = useState(null);  // current pathway doc OR 'new'
  const [draft, setDraft] = useState({ pathway: '', patterns: '', severity: 'high', color_tag: 'red', active: true, notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Test panel
  const [testInci, setTestInci] = useState('Glycerin\nPerfluorooctanoic acid\nPEG-40 hydrogenated castor oil\nLinalool');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/hazard/contaminants`, { withCredentials: true });
      setPathways(r.data.pathways || []);
    } catch (e) { /* keep */ }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing('new');
    setDraft({ pathway: '', patterns: '', severity: 'high', color_tag: 'red', active: true, notes: '' });
    setError('');
  };
  const startEdit = (p) => {
    setEditing(p);
    setDraft({ ...p, patterns: (p.patterns || []).join('\n') });
    setError('');
  };

  const save = async () => {
    if (!draft.pathway?.trim()) { setError('Pathway name required.'); return; }
    const patternList = draft.patterns.split('\n').map(s => s.trim()).filter(Boolean);
    if (patternList.length === 0) { setError('At least one regex pattern required.'); return; }
    setSaving(true); setError('');
    try {
      const body = {
        pathway: draft.pathway.trim(),
        patterns: patternList,
        severity: draft.severity, color_tag: draft.color_tag,
        active: !!draft.active, notes: draft.notes || null,
        display_order: draft.display_order ?? 100,
      };
      if (editing === 'new') {
        await axios.post(`${API_URL}/api/admin/hazard/contaminants`, body, { withCredentials: true });
      } else {
        await axios.put(`${API_URL}/api/admin/hazard/contaminants/${editing.id}`, body, { withCredentials: true });
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete pathway "${p.pathway}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/hazard/contaminants/${p.id}`, { withCredentials: true });
      await load();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const ingredients = testInci.split('\n').map(s => s.trim()).filter(Boolean);
      const r = await axios.post(`${API_URL}/api/admin/hazard/contaminants/test`, { ingredients }, { withCredentials: true });
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ error: e?.response?.data?.detail || e.message });
    } finally { setTesting(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 card-flat p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#111827]">Contaminant pathways ({pathways.length})</h3>
          <Button onClick={startNew} className="h-8 text-xs rounded bg-[#FF4E6A] hover:bg-[#E63E5A] text-white" data-testid="contaminant-new"><Plus size={12} className="mr-1" weight="bold" /> New pathway</Button>
        </div>
        <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
          {pathways.map(p => (
            <div key={p.id} className="border border-gray-200 rounded p-3" data-testid={`contaminant-row-${p.id}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-[#111827] text-sm">{p.pathway}</p>
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${p.severity === 'high' ? 'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]' : p.severity === 'medium' ? 'bg-[#FEF3C7] text-[#723B13] border-[#F3CC64]' : 'bg-gray-100 text-[#4B5563] border-gray-300'}`}>{p.severity}</span>
                    {!p.active && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-gray-100 text-[#4B5563] border-gray-300">disabled</span>}
                  </div>
                  <p className="text-[11px] text-[#4B5563] mt-1 font-mono break-all">{(p.patterns || []).slice(0, 4).join(' · ')}{p.patterns?.length > 4 ? ` … (+${p.patterns.length - 4})` : ''}</p>
                </div>
                <Button onClick={() => startEdit(p)} variant="outline" className="h-7 text-xs rounded" data-testid={`contaminant-edit-${p.id}`}><Pencil size={11} className="mr-1" /> Edit</Button>
                <Button onClick={() => remove(p)} variant="outline" className="h-7 text-xs rounded text-[#9B1C1C] border-[#F8B4B4] hover:bg-[#FDE8E8]" data-testid={`contaminant-delete-${p.id}`}><Trash size={11} /></Button>
              </div>
            </div>
          ))}
          {pathways.length === 0 && <p className="text-center text-xs text-[#4B5563] py-6">No pathways yet.</p>}
        </div>
      </div>

      <div className="space-y-4">
        {/* Editor */}
        {editing && (
          <div className="card-flat p-4 bg-white" data-testid="contaminant-editor">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-[#111827]">{editing === 'new' ? 'New pathway' : `Edit · ${editing.pathway}`}</h3>
              <button onClick={() => setEditing(null)} className="text-[#4B5563]"><X size={16} /></button>
            </div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Pathway name</Label>
            <Input value={draft.pathway} onChange={(e) => setDraft(d => ({ ...d, pathway: e.target.value }))} className="h-8 text-sm mt-1 mb-2" data-testid="contaminant-pathway" />
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Regex patterns (one per line)</Label>
            <Textarea value={draft.patterns} onChange={(e) => setDraft(d => ({ ...d, patterns: e.target.value }))} rows={6} className="text-[11px] font-mono mt-1 mb-2" data-testid="contaminant-patterns" />
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Severity</Label>
                <select value={draft.severity} onChange={(e) => setDraft(d => ({ ...d, severity: e.target.value }))} className="mt-1 w-full h-8 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="contaminant-severity">
                  {SEVERITY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Color</Label>
                <select value={draft.color_tag} onChange={(e) => setDraft(d => ({ ...d, color_tag: e.target.value }))} className="mt-1 w-full h-8 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="contaminant-color">
                  {COLOR_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <label className="flex items-center gap-1.5 text-[11px] text-[#4B5563] mb-2">
              <input type="checkbox" checked={draft.active} onChange={(e) => setDraft(d => ({ ...d, active: e.target.checked }))} data-testid="contaminant-active" />
              Active (included in Risk Screen scans)
            </label>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">Notes (optional)</Label>
            <Input value={draft.notes || ''} onChange={(e) => setDraft(d => ({ ...d, notes: e.target.value }))} className="h-8 text-sm mt-1 mb-2" data-testid="contaminant-notes" />
            {error && <p className="text-[11px] text-[#9B1C1C] mb-2" data-testid="contaminant-error">{error}</p>}
            <Button onClick={save} disabled={saving} className="w-full h-9 rounded bg-[#FF4E6A] hover:bg-[#E63E5A] text-white text-sm font-semibold" data-testid="contaminant-save">
              <FloppyDisk size={14} className="mr-1.5" weight="bold" /> {saving ? 'Saving…' : 'Save pathway'}
            </Button>
          </div>
        )}

        {/* Test panel */}
        <div className="card-flat p-4 bg-white" data-testid="contaminant-test-panel">
          <h3 className="text-sm font-bold text-[#111827] mb-2 flex items-center gap-1.5"><Flask size={14} weight="fill" className="text-[#FF4E6A]" /> Test regex</h3>
          <p className="text-[11px] text-[#4B5563] mb-2">Paste INCI rows. Dry-run match against the active pathway set.</p>
          <Textarea value={testInci} onChange={(e) => setTestInci(e.target.value)} rows={4} className="text-[12px] mb-2" data-testid="contaminant-test-input" />
          <Button onClick={runTest} disabled={testing} variant="outline" className="w-full h-8 text-xs rounded" data-testid="contaminant-test-run"><ArrowsClockwise size={12} className="mr-1" weight="bold" /> {testing ? 'Testing…' : 'Run dry-run'}</Button>
          {testResult && (
            <div className="mt-2 text-[11px]" data-testid="contaminant-test-result">
              {testResult.error ? <p className="text-[#9B1C1C]">{testResult.error}</p> :
              Object.keys(testResult.matched || {}).length === 0 ? <p className="text-[#4B5563]">No matches across {testResult.tested_pathways?.length || 0} pathway(s).</p> :
              Object.entries(testResult.matched).map(([k, v]) => (
                <div key={k} className="border-t border-gray-100 py-1.5">
                  <p className="font-bold text-[#9B1C1C]">{k}</p>
                  <p className="text-[10px] text-[#4B5563]">{v.join(' · ')}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- AUDIT TAB ---------------- */

function AuditTab() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = filter ? `?entity_type=${filter}` : '';
        const r = await axios.get(`${API_URL}/api/admin/hazard/audit${params}`, { withCredentials: true });
        setEvents(r.data.events || []);
      } catch (e) { /* keep */ }
      finally { setLoading(false); }
    })();
  }, [filter]);

  return (
    <div className="card-flat p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-[#111827]">Hazard Intelligence audit log</h3>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-8 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="audit-filter">
          <option value="">All events</option>
          <option value="ingredient">Ingredients only</option>
          <option value="contaminant_pattern">Contaminants only</option>
        </select>
      </div>
      {loading ? <p className="text-sm text-[#4B5563] py-8 text-center">Loading…</p> : events.length === 0 ? <p className="text-sm text-[#4B5563] py-8 text-center">No audit events yet.</p> : (
        <div className="border border-gray-200 rounded overflow-hidden">
          <table className="w-full text-[12px]">
            <thead className="bg-gray-50">
              <tr className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563]">
                <th className="text-left py-2 px-3">When</th>
                <th className="text-left py-2 px-3">Who</th>
                <th className="text-left py-2 px-3">Action</th>
                <th className="text-left py-2 px-3">Entity</th>
                <th className="text-left py-2 px-3">Changes</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => (
                <tr key={i} className="border-t border-gray-100" data-testid={`audit-row-${i}`}>
                  <td className="py-1.5 px-3 text-[11px] text-[#4B5563] tabular-nums">{new Date(e.at).toLocaleString()}</td>
                  <td className="py-1.5 px-3 text-[11px] font-semibold text-[#111827]">{e.by_name || '—'}</td>
                  <td className="py-1.5 px-3 text-[11px] text-[#4B5563]"><span className="font-mono">{e.action}</span></td>
                  <td className="py-1.5 px-3 text-[11px] text-[#111827]">{e.entity_id}</td>
                  <td className="py-1.5 px-3 text-[10px] text-[#4B5563]">{JSON.stringify(e.changes).slice(0, 120)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- PAGE ROOT ---------------- */

const TABS = [
  { id: 'klimisch',    label: 'Klimisch',        icon: Brain },
  { id: 'endpoints',   label: 'Hazard endpoints', icon: Pencil },
  { id: 'contaminants', label: 'Contaminants',    icon: Flask },
  { id: 'audit',       label: 'Audit',           icon: ClockCounterClockwise },
];

export default function HazardIntelligencePage() {
  const [tab, setTab] = useState('klimisch');
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Brain size={22} className="text-[#FF4E6A]" weight="duotone" />
            <h1 className="text-2xl font-bold tracking-tight text-[#111827]">Centralized Hazard Intelligence</h1>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded ml-2">internal · admin only</span>
          </div>
          <p className="text-sm text-[#4B5563]">
            Klimisch reliability scoring, hazard endpoint curation, and contaminant pathway intelligence — the source of truth for every Risk Memo, TRA/QRA, and CPSR generated downstream.
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Link to="/assessor"><Button variant="outline" className="h-8 text-xs rounded" data-testid="hazard-to-assessor">← Assessor Console</Button></Link>
            <Link to="/assessor/exposure"><Button variant="outline" className="h-8 text-xs rounded" data-testid="hazard-to-exposure">Exposure &amp; MoS Engine →</Button></Link>
            <Link to="/assessor/noael-extraction"><Button variant="outline" className="h-8 text-xs rounded" data-testid="hazard-to-noael">NOAEL Extraction →</Button></Link>
          </div>
        </div>

        <div className="flex border-b border-gray-200 mb-4 overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon;
            const isActive = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${isActive ? 'border-[#FF4E6A] text-[#111827]' : 'border-transparent text-[#4B5563] hover:text-[#111827]'}`} data-testid={`hazard-tab-${t.id}`}>
                <Icon size={16} weight={isActive ? 'duotone' : 'regular'} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'klimisch' && <KlimischTab />}
        {tab === 'endpoints' && <EndpointsTab />}
        {tab === 'contaminants' && <ContaminantsTab />}
        {tab === 'audit' && <AuditTab />}
      </main>
    </div>
  );
}
